import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { parseMarkdown } from '../utils/markdownParser';
import RecentPostsSidebar from '../components/RecentPostsSidebar';

function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const badges = {
      ongoing: { text: 'Ongoing', class: 'bg-success', icon: '🟢' },
      completed: { text: 'Completed', class: 'bg-primary', icon: '🔵' },
      planned: { text: 'Planned', class: 'bg-warning', icon: '🟡' }
    };
    const badge = badges[status] || badges.planned;
    return (
      <span className={`badge ${badge.class} text-white px-4 py-2`} style={{ fontSize: '1rem' }}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: true,
    adaptiveHeight: true
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <h2>Project not found</h2>
          <Link to="/projects" className="btn btn-primary mt-3">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  // Collect all images: featured image + gallery images for slider
  const allImages = [];
  
  // Add featured image first
  if (project.featured_image) {
    const featuredUrl = project.featured_image.startsWith('/uploads/') 
      ? project.featured_image 
      : `/uploads/${project.featured_image}`;
    allImages.push({ image: featuredUrl, isFeatured: true });
  }
  
  // Add gallery images
  const galleryImages = project.images || [];
  galleryImages.forEach(img => {
    const imgUrl = img.image.startsWith('/uploads/') 
      ? img.image 
      : `/uploads/${img.image}`;
    allImages.push({ image: imgUrl, caption: img.caption });
  });
  
  // If no images, use default
  if (allImages.length === 0) {
    allImages.push({ image: '/img/Gallery/opd1.jpg' });
  }
  
  const featuredImageUrl = allImages[0]?.image || '/img/Gallery/opd1.jpg';

  return (
    <>
      {/* Page Header */}
      <div
        className="container-fluid"
        style={{
          background: 'linear-gradient(rgba(0, 61, 130, 0.9), rgba(0, 61, 130, 0.9)), url(/img/hospital-building.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '4rem 0 3rem',
        }}
      >
        <div className="container">
          <div className="text-center">
            <h2 className="text-white mb-3" style={{ fontSize: '2.5rem', fontWeight: '600', fontFamily: "'Lora', 'Georgia', serif", textTransform: 'uppercase', letterSpacing: '1px' }}>
              PROJECTS & INITIATIVES
            </h2>
            <h1 className="text-white mb-0" style={{ fontSize: '2.5rem', fontWeight: '600', fontFamily: "'Lora', 'Georgia', serif" }}>
              {project.title}
            </h1>
            <p className="text-white mt-3 mb-0" style={{ fontSize: '1.1rem', opacity: 0.9 }}>
              {project.short_description || 'Project details'}
            </p>
            <nav aria-label="breadcrumb" className="mt-3 d-flex justify-content-center">
              <ol className="breadcrumb mb-0" style={{ backgroundColor: 'transparent', padding: 0 }}>
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none' }}>
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/projects" style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none' }}>
                    Projects
                  </Link>
                </li>
                <li className="breadcrumb-item active text-white" aria-current="page">
                  {project.title}
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* Project Content */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa', minHeight: '60vh' }}>
        <div className="container">
          <div className="row">
            {/* Main Content */}
            <div className="col-lg-8">
              {/* Banner Image Slider */}
              <div className="mb-4 project-detail-banner" style={{ borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
                {allImages.length > 1 ? (
                  <Slider {...sliderSettings}>
                    {allImages.map((img, index) => (
                      <div key={index}>
                        <div style={{ height: '400px', overflow: 'hidden' }}>
                          <img
                            src={img.image}
                            alt={img.caption || project.title}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block'
                            }}
                            onError={(e) => {
                              e.target.src = '/img/Gallery/opd1.jpg';
                            }}
                          />
                        </div>
                        {img.caption && (
                          <p className="text-center mt-2 text-muted small">{img.caption}</p>
                        )}
                      </div>
                    ))}
                  </Slider>
                ) : (
                  <img
                    src={featuredImageUrl}
                    alt={project.title}
                    style={{
                      width: '100%',
                      height: '400px',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                    onError={(e) => {
                      e.target.src = '/img/Gallery/opd1.jpg';
                    }}
                  />
                )}
              </div>

              {/* Status and Dates */}
              <div className="mb-4 project-status-dates d-flex flex-wrap gap-3 align-items-center">
                {getStatusBadge(project.status)}
                <div className="text-muted">
                  <i className="bi bi-calendar3 me-1"></i>
                  <strong>Start Date:</strong> {formatDate(project.start_date)}
                </div>
                {project.end_date && (
                  <div className="text-muted">
                    <i className="bi bi-calendar-check me-1"></i>
                    <strong>End Date:</strong> {formatDate(project.end_date)}
                  </div>
                )}
              </div>

              {/* Progress Bar for Ongoing Projects */}
              {project.status === 'ongoing' && project.progress !== null && (
                <div className="mb-4 p-4 bg-white rounded" style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                  <h5 className="mb-3">Project Progress</h5>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Completion</span>
                    <span className="fw-bold text-success">{project.progress}%</span>
                  </div>
                  <div className="progress" style={{ height: '20px', borderRadius: '10px' }}>
                    <div
                      className="progress-bar bg-success progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${project.progress}%` }}
                      aria-valuenow={project.progress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {project.progress}%
                    </div>
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="mb-4 p-4 bg-white rounded" style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                <h4 className="mb-3" style={{ color: '#003d82', fontWeight: '600' }}>About This Project</h4>
                <div 
                  style={{ 
                    fontSize: '1.05rem', 
                    lineHeight: '1.8', 
                    color: '#333'
                  }}
                  dangerouslySetInnerHTML={{ __html: parseMarkdown(project.description || 'No description available.') }}
                />
              </div>

              {/* Beneficiaries */}
              {project.beneficiaries && (
                <div className="mb-4 p-4 bg-white rounded" style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                  <h4 className="mb-3" style={{ color: '#003d82', fontWeight: '600' }}>
                    <i className="bi bi-people-fill me-2"></i>Beneficiaries
                  </h4>
                  <div 
                    style={{ 
                      fontSize: '1.05rem', 
                      lineHeight: '1.8', 
                      color: '#333'
                    }}
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(project.beneficiaries) }}
                  />
                </div>
              )}

              {/* Outcomes */}
              {project.outcomes && (
                <div className="mb-4 p-4 bg-white rounded" style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                  <h4 className="mb-3" style={{ color: '#003d82', fontWeight: '600' }}>
                    <i className="bi bi-trophy-fill me-2"></i>Outcomes & Impact
                  </h4>
                  <div 
                    style={{ 
                      fontSize: '1.05rem', 
                      lineHeight: '1.8', 
                      color: '#333'
                    }}
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(project.outcomes) }}
                  />
                </div>
              )}


              {/* Partners */}
              {project.partners && project.partners.length > 0 && (
                <div className="mb-4 p-4 bg-white rounded" style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                  <h4 className="mb-3" style={{ color: '#003d82', fontWeight: '600' }}>
                    <i className="bi bi-handshake-fill me-2"></i>Partners
                  </h4>
                  <div className="row g-3 project-partners-grid">
                    {project.partners.map((partner, index) => (
                      <div key={index} className="col-md-4 col-6">
                        <div className="text-center p-3 border rounded">
                          {partner.partner_logo ? (
                            <img
                              src={partner.partner_logo.startsWith('/uploads/') ? partner.partner_logo : `/uploads/${partner.partner_logo}`}
                              alt={partner.partner_name}
                              style={{ maxWidth: '100%', maxHeight: '80px', objectFit: 'contain' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <p className="mb-0 fw-bold">{partner.partner_name}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents */}
              {project.documents && project.documents.length > 0 && (
                <div className="mb-4 p-4 bg-white rounded" style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                  <h4 className="mb-3" style={{ color: '#003d82', fontWeight: '600' }}>
                    <i className="bi bi-file-earmark-pdf-fill me-2"></i>Documents & Reports
                  </h4>
                  <div className="list-group">
                    {project.documents.map((doc, index) => (
                      <a
                        key={index}
                        href={doc.document_path.startsWith('/uploads/') ? doc.document_path : `/uploads/${doc.document_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                      >
                        <span>
                          <i className="bi bi-file-earmark-pdf me-2"></i>
                          {doc.document_name}
                        </span>
                        <i className="bi bi-download"></i>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="col-lg-4 project-detail-sidebar">
              <div style={{ position: 'sticky', top: '20px', zIndex: 10 }}>
                <RecentPostsSidebar type="projects" limit={5} excludeId={id} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default ProjectDetail;
